'use client';

/**
 * MergePreview Component
 *
 * Side-by-side record comparison for merging with per-field strategy selection.
 * Part of the identity resolution component suite.
 */

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { alpha, styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { motion } from 'framer-motion';
import { forwardRef, useCallback, useMemo, useState } from 'react';
import { baseColors, getNeonStyles, type NeonAccent } from '../../theme/palette';
import { createNeonGlow, glassEffects } from '../../theme/shadows';
import { motionPresets, transitionPresets } from '../../theme/transitions';
import type { MergePreviewProps, MergeStrategyType } from '../../types/identityResolution';
import { MergeStrategySelector } from './MergeStrategySelector';

// ============================================================================
// Styled Components
// ============================================================================

interface StyledContainerProps {
  $isDark: boolean;
  $neonAccent?: NeonAccent;
}

const StyledContainer = styled(motion.div, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledContainerProps>(({ $isDark, $neonAccent }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  return {
    position: 'relative',
    borderRadius: 16,
    overflow: 'hidden',
    transition: transitionPresets.card,

    // Use neon styling if accent is set, otherwise use glass styling
    ...(neonStyles
      ? {
          background: neonStyles.background,
          border: `1px solid ${neonStyles.border}`,
          boxShadow: createNeonGlow($neonAccent!, 'subtle'),
        }
      : {
          background: glass.background,
          backdropFilter: glass.backdropBlur,
          border: `1px solid ${glass.border}`,
          boxShadow: glass.shadow,
        }),
  };
});

const StyledHeader = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $isDark: boolean; $accentColor?: string }>(({ theme, $isDark }) => ({
  padding: theme.spacing(2.5, 3),
  borderBottom: `1px solid ${$isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),

  '& .header-title': {
    fontSize: '1.125rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
  },

  '& .header-subtitle': {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
}));

const StyledRecordHeaders = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: theme.spacing(2),
  padding: theme.spacing(2, 3),
  background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
  borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)'}`,
}));

interface StyledRecordHeaderProps {
  $side: 'primary' | 'secondary';
  $isDark: boolean;
}

const StyledRecordHeader = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledRecordHeaderProps>(({ theme, $side, $isDark }) => {
  const color =
    $side === 'primary' ? baseColors.primary[$isDark ? 400 : 500] : baseColors.secondary[$isDark ? 400 : 500];

  return {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),

    '& .record-badge': {
      width: 8,
      height: 8,
      borderRadius: '50%',
      background: color,
      flexShrink: 0,
    },

    '& .record-label': {
      fontSize: '0.75rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color,
    },

    '& .record-id': {
      fontSize: '0.75rem',
      color: theme.palette.text.secondary,
      marginLeft: 'auto',
      fontFamily: 'monospace',
    },
  };
});

const StyledContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 3, 3),
}));

const StyledFieldsSection = styled(Box)(({ theme }) => ({
  '& .section-title': {
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.5),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),

    '&::after': {
      content: '""',
      flex: 1,
      height: 1,
      background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
    },
  },
}));

const StyledFooter = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  borderTop: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
}));

const StyledSummary = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),

  '& .summary-item': {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),

    '& .item-count': {
      fontSize: '1rem',
      fontWeight: 700,
      color: theme.palette.text.primary,
    },

    '& .item-label': {
      fontSize: '0.75rem',
      color: theme.palette.text.secondary,
    },
  },
}));

interface StyledActionButtonProps {
  $variant: 'primary' | 'secondary';
  $isDark: boolean;
  $accentColor?: string;
}

const StyledActionButton = styled(motion.button, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledActionButtonProps>(({ theme, $variant, $isDark, $accentColor }) => {
  const isPrimary = $variant === 'primary';
  const color = $accentColor || baseColors.primary[$isDark ? 400 : 500];

  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: isPrimary ? '12px 24px' : '12px 20px',
    borderRadius: 10,
    fontSize: '0.875rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: transitionPresets.button,
    fontFamily: theme.typography.fontFamily,

    ...(isPrimary
      ? {
          background: color,
          color: '#fff',
          border: 'none',
          boxShadow: `0 4px 14px ${alpha(color, 0.35)}`,

          '&:hover': {
            boxShadow: `0 6px 20px ${alpha(color, 0.45)}`,
          },
        }
      : {
          background: 'transparent',
          color: theme.palette.text.secondary,
          border: `1px solid ${$isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,

          '&:hover': {
            background: $isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
          },
        }),

    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  };
});

const StyledLoadingOverlay = styled(motion.div)(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(4px)',
  zIndex: 10,

  '& .loading-text': {
    fontSize: '1rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
}));

// ============================================================================
// Component
// ============================================================================

export const MergePreview = forwardRef<HTMLDivElement, MergePreviewProps>(
  (
    {
      primaryRecord,
      secondaryRecord,
      fields,
      mergeStrategies,
      onStrategyChange,
      onMerge,
      onCancel,
      loading = false,
      neonAccent,
    },
    ref,
  ) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const [merging, setMerging] = useState(false);

    // Get accent color
    const accentColor = neonAccent ? getNeonStyles(neonAccent, isDark).base : undefined;

    // Calculate summary
    const summary = useMemo(() => {
      let primaryCount = 0;
      let secondaryCount = 0;
      let mergeCount = 0;
      let customCount = 0;

      for (const field of fields) {
        const strategy = mergeStrategies[field.name] || 'KEEP_PRIMARY';
        switch (strategy) {
          case 'KEEP_PRIMARY':
            primaryCount++;
            break;
          case 'KEEP_SECONDARY':
            secondaryCount++;
            break;
          case 'MERGE':
            mergeCount++;
            break;
          case 'CUSTOM':
            customCount++;
            break;
        }
      }

      return { primaryCount, secondaryCount, mergeCount, customCount };
    }, [fields, mergeStrategies]);

    // Handlers
    const handleStrategyChange = useCallback(
      (fieldName: string, strategy: MergeStrategyType, customValue?: unknown) => {
        onStrategyChange(fieldName, strategy, customValue);
      },
      [onStrategyChange],
    );

    const handleMerge = useCallback(async () => {
      setMerging(true);
      try {
        await onMerge();
      } finally {
        setMerging(false);
      }
    }, [onMerge]);

    const isLoading = loading || merging;

    return (
      <StyledContainer
        ref={ref}
        $isDark={isDark}
        $neonAccent={neonAccent}
        initial={motionPresets.fadeInUp.initial}
        animate={motionPresets.fadeInUp.animate}
        transition={motionPresets.fadeInUp.transition}
      >
        {/* Loading Overlay */}
        {isLoading && (
          <StyledLoadingOverlay initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <Typography className="loading-text">Merging records...</Typography>
          </StyledLoadingOverlay>
        )}

        {/* Header */}
        <StyledHeader $isDark={isDark} $accentColor={accentColor}>
          <Box>
            <Typography className="header-title">Merge Preview</Typography>
            <Typography className="header-subtitle">Select how to merge each field from both records</Typography>
          </Box>
        </StyledHeader>

        {/* Record Headers */}
        <StyledRecordHeaders>
          <StyledRecordHeader $side="primary" $isDark={isDark}>
            <span className="record-badge" />
            <span className="record-label">Primary Record</span>
            <span className="record-id">#{primaryRecord.id.slice(0, 8)}</span>
          </StyledRecordHeader>

          <StyledRecordHeader $side="secondary" $isDark={isDark}>
            <span className="record-badge" />
            <span className="record-label">Secondary Record</span>
            <span className="record-id">#{secondaryRecord.id.slice(0, 8)}</span>
          </StyledRecordHeader>
        </StyledRecordHeaders>

        {/* Field Strategies */}
        <StyledContent>
          <StyledFieldsSection>
            <Typography className="section-title">Field Merge Strategies</Typography>

            <Stack spacing={2}>
              {fields.map((field, index) => (
                <motion.div
                  key={field.name}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05, duration: 0.2 }}
                >
                  <MergeStrategySelector
                    field={field}
                    primaryValue={primaryRecord.data[field.name]}
                    secondaryValue={secondaryRecord.data[field.name]}
                    strategy={mergeStrategies[field.name] || 'KEEP_PRIMARY'}
                    onStrategyChange={(strategy, customValue) =>
                      handleStrategyChange(field.name, strategy, customValue)
                    }
                    disabled={isLoading}
                    neonAccent={neonAccent}
                  />
                </motion.div>
              ))}
            </Stack>
          </StyledFieldsSection>
        </StyledContent>

        {/* Footer */}
        <StyledFooter>
          <StyledSummary>
            <Box className="summary-item">
              <span className="item-count">{summary.primaryCount}</span>
              <span className="item-label">from primary</span>
            </Box>
            <Box className="summary-item">
              <span className="item-count">{summary.secondaryCount}</span>
              <span className="item-label">from secondary</span>
            </Box>
            {summary.mergeCount > 0 && (
              <Box className="summary-item">
                <span className="item-count">{summary.mergeCount}</span>
                <span className="item-label">merged</span>
              </Box>
            )}
            {summary.customCount > 0 && (
              <Box className="summary-item">
                <span className="item-count">{summary.customCount}</span>
                <span className="item-label">custom</span>
              </Box>
            )}
          </StyledSummary>

          <Stack direction="row" spacing={1.5}>
            {onCancel && (
              <StyledActionButton
                $variant="secondary"
                $isDark={isDark}
                onClick={onCancel}
                disabled={isLoading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Cancel
              </StyledActionButton>
            )}

            <StyledActionButton
              $variant="primary"
              $isDark={isDark}
              $accentColor={accentColor}
              onClick={handleMerge}
              disabled={isLoading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {isLoading ? 'Merging...' : 'Merge Records'}
              {!isLoading && (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path
                    d="M8 3v10M5 6l3-3 3 3M5 10l3 3 3-3"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </StyledActionButton>
          </Stack>
        </StyledFooter>
      </StyledContainer>
    );
  },
);

MergePreview.displayName = 'MergePreview';

export default MergePreview;
